import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { DisconnectedConnectionBanner } from '../../../sessions/components/DisconnectedConnectionBanner';
import { ConnectionTabFailureBanner } from '../../../connection-diagnostics/components/ConnectionTabFailureBanner';
import { RenameTabNotice } from '../../../navigational-features/rename/RenameTabNotice';
import { useRenameTabNoticeStore } from '../../../navigational-features/rename/renameTabNoticeStore';
import type { QueryTab } from '../../core/types/workspaceTypes';

type QueryEditorTabBannersProps = {
  tabId: string;
  connectionId: string | undefined;
  tabDatabase: string | undefined;
  connectionName: string | undefined;
  isDisconnected: boolean;
  bannerDismissed: boolean;
  setBannerDismissed: (dismissed: boolean) => void;
  handleReconnect: () => Promise<boolean>;
  connectionFailure: NonNullable<QueryTab['content']>['connectionFailure'];
};

/**
 * The banner strip at the top of a query editor tab: the disconnected-
 * connection banner, the Phase-3 Slice 34 rename courtesy notices, and the
 * Slice 6 per-tab connection-diagnostics failure banner. JSX moved verbatim
 * from QueryEditorTabContent (with `tabContent.connectionFailure` flowing in
 * as the `connectionFailure` prop).
 */
export function QueryEditorTabBanners({
  tabId,
  connectionId,
  tabDatabase,
  connectionName,
  isDisconnected,
  bannerDismissed,
  setBannerDismissed,
  handleReconnect,
  connectionFailure,
}: QueryEditorTabBannersProps) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  // Show banner only if disconnected and not dismissed
  const showDisconnectedBanner = isDisconnected && !bannerDismissed;

  // Phase-3 Slice 34 — rename courtesy notices for this tab. The store is
  // populated by `renameService.applyRename` after a successful rename when
  // this buffer still references the old identifier; banners are dismissible
  // and persist for the session.
  const renameNotices = useRenameTabNoticeStore((s) => s.byTabId[tabId]);

  return (
    <>
      {/* Disconnected Connection Banner */}
      {showDisconnectedBanner && (
        <DisconnectedConnectionBanner
          connectionName={connectionName || 'Unknown connection'}
          onReconnect={handleReconnect}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* Rename courtesy notices — Slice 34 */}
      {renameNotices?.map((notice) => (
        <RenameTabNotice
          key={`${notice.oldName.foldedSchema ?? ''}.${notice.oldName.foldedName ?? notice.oldName.name}->${notice.newName.foldedName ?? notice.newName.name}`}
          notice={notice}
        />
      ))}

      {/* Slice 6 — Connection Diagnostics: per-tab failure banner */}
      {connectionId && connectionFailure && !connectionFailure.dismissedAt && (() => {
        const failure = connectionFailure;
        return (
          <ConnectionTabFailureBanner
            connectionId={connectionId}
            database={failure.scopeDatabase ?? tabDatabase ?? ''}
            sqlErrorCode={failure.sqlErrorCode}
            onDismiss={() =>
              updateTabContent(tabId, {
                connectionFailure: { ...failure, dismissedAt: Date.now() },
              })
            }
          />
        );
      })()}
    </>
  );
}
